import { useEffect, useState } from "react";

export function CandleSplash() {
  const [stage, setStage] = useState<"dark" | "flame" | "candle" | "text" | "fadeout" | "done">("dark");

  useEffect(() => {
    const t1 = setTimeout(() => setStage("flame"),   500);
    const t2 = setTimeout(() => setStage("candle"),  900);
    const t3 = setTimeout(() => setStage("text"),    1500);
    const t4 = setTimeout(() => setStage("fadeout"), 3600); // Begin fade-out
    const t5 = setTimeout(() => setStage("done"),    4800); // Guaranteed unmount fallback
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); clearTimeout(t5); };
  }, []);

  if (stage === "done") return null;

  const fading       = stage === "fadeout";
  const flameVisible  = stage === "flame" || stage === "candle" || stage === "text" || fading;
  const candleVisible = stage === "candle" || stage === "text" || fading;
  const textVisible   = stage === "text" || fading;

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "#080808",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        opacity: fading ? 0 : 1,
        transition: fading ? "opacity 1.2s cubic-bezier(0.4, 0, 0.2, 1)" : "none",
        userSelect: "none", pointerEvents: "none",
      }}
      onTransitionEnd={() => { if (fading) setStage("done"); }}
    >
      <style>{`
        /* Only opacity-based animations - ZERO transforms to prevent vibration */
        @keyframes flicker {
          0%,100% { opacity: 0.90; }
          30%      { opacity: 1.00; }
          60%      { opacity: 0.85; }
        }
        @keyframes glow-pulse {
          0%,100% { opacity: 0.5; }
          50%      { opacity: 0.75; }
        }
        @keyframes candle-glow {
          0%,100% { opacity: 0.2; }
          50%      { opacity: 0.35; }
        }
      `}</style>

      {/* Full scene container */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>

        {/* ── FLAME GROUP ── */}
        <div style={{
          position: "relative",
          width: 60, height: 80,
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "flex-end",
          opacity: flameVisible ? 1 : 0,
          transition: "opacity 0.2s ease",
        }}>
          {/* Distant ambient halo */}
          <div style={{
            position: "absolute",
            bottom: -20, left: "50%",
            transform: "translateX(-50%)",
            width: 200, height: 200,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,130,0,0.22) 0%, rgba(255,80,0,0.08) 45%, transparent 70%)",
            animation: "glow-pulse 2.5s ease-in-out infinite",
          }} />

          {/* Near warm glow directly under flame */}
          <div style={{
            position: "absolute",
            bottom: -2, left: "50%",
            transform: "translateX(-50%)",
            width: 80, height: 80,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,160,0,0.45) 0%, rgba(255,100,0,0.15) 50%, transparent 75%)",
            filter: "blur(6px)",
            animation: "glow-pulse 2.5s ease-in-out infinite",
          }} />

          {/* Outer flame teardrop (orange) */}
          <div style={{
            width: 24,
            height: 54,
            background: "linear-gradient(to top, #D44000 0%, #FF7000 30%, #FFC000 65%, #FFF4A0 90%, rgba(255,255,220,0) 100%)",
            borderRadius: "50% 50% 38% 38% / 55% 55% 35% 35%",
            boxShadow: "0 0 20px 6px rgba(255,120,0,0.55), 0 0 55px 22px rgba(255,80,0,0.22)",
            animation: "flicker 2.2s ease-in-out infinite",
            position: "relative",
            zIndex: 2,
          }}>
            {/* Inner bright core */}
            <div style={{
              position: "absolute",
              bottom: 4, left: "50%",
              transform: "translateX(-50%)",
              width: 10, height: 28,
              background: "linear-gradient(to top, #002AFF 0%, #FF3300 22%, #FFB800 55%, #FFFFF0 88%, transparent 100%)",
              borderRadius: "50% 50% 38% 38% / 55% 55% 35% 35%",
              opacity: 0.92,
            }} />
          </div>
        </div>

        {/* ── CANDLE BODY GROUP ── */}
        <div style={{
          opacity: candleVisible ? 1 : 0,
          transition: "opacity 1.8s ease",
        }}>
          {/* Wick */}
          <div style={{
            width: 2.5, height: 14,
            background: "linear-gradient(to bottom, #1a1a1a, #3a2a1a)",
            borderRadius: "2px 2px 0 0",
            margin: "0 auto",
            position: "relative", zIndex: 3,
          }} />

          {/* Candle top — melted wax pool ellipse */}
          <div style={{
            width: 68, height: 14,
            borderRadius: "50%",
            background: "radial-gradient(ellipse, rgba(255,220,130,0.8) 0%, #F5E2C0 55%, #D8C49A 100%)",
            margin: "0 auto",
            boxShadow: "0 0 12px 4px rgba(255,160,0,0.25)",
            animation: "candle-glow 2.5s ease-in-out infinite",
            position: "relative",
            marginBottom: -6,
            zIndex: 2,
          }} />

          {/* Main wax cylinder */}
          <div style={{
            width: 68,
            height: 150,
            borderRadius: "4px 4px 8px 8px",
            background: `
              linear-gradient(to right,
                #A8865A 0%,
                #C9A97A 6%,
                #EDD9B5 22%,
                #FDF2DE 40%,
                #FFF8F0 50%,
                #FDF2DE 60%,
                #EDD9B5 78%,
                #C2975E 94%,
                #A07040 100%
              )
            `,
            boxShadow: "inset 0 0 20px rgba(255,160,0,0.08), 0 10px 30px rgba(0,0,0,0.8), 0 0 25px rgba(255,130,0,0.15)",
            position: "relative",
            overflow: "hidden",
          }}>
            {/* Top warm light cast from flame */}
            <div style={{
              position: "absolute",
              top: 0, left: 0, right: 0,
              height: 40,
              background: "linear-gradient(to bottom, rgba(255,190,80,0.28), transparent)",
              animation: "candle-glow 2.5s ease-in-out infinite",
            }} />
            {/* Wax drip left */}
            <div style={{
              position: "absolute",
              top: 8, left: 10,
              width: 5, height: 22,
              background: "linear-gradient(to bottom, rgba(255,240,200,0.6), transparent)",
              borderRadius: "0 0 4px 4px",
            }} />
            {/* Wax drip right */}
            <div style={{
              position: "absolute",
              top: 14, right: 14,
              width: 4, height: 16,
              background: "linear-gradient(to bottom, rgba(255,240,200,0.4), transparent)",
              borderRadius: "0 0 4px 4px",
            }} />
          </div>

          {/* Ground shadow */}
          <div style={{
            width: 55, height: 10,
            borderRadius: "50%",
            background: "radial-gradient(ellipse, rgba(0,0,0,0.85) 30%, transparent 80%)",
            margin: "-4px auto 0",
          }} />
        </div>

        {/* ── BRAND TEXT ── */}
        <div style={{
          marginTop: 36,
          display: "flex", flexDirection: "column",
          alignItems: "center", gap: 10,
          opacity: textVisible ? 1 : 0,
          transition: "opacity 2s ease",
        }}>
          <h1 style={{
            fontFamily: "Georgia, 'Times New Roman', serif",
            fontSize: 22, fontWeight: 600,
            color: "#C8A165",
            letterSpacing: "0.38em",
            textTransform: "uppercase",
            margin: 0,
          }}>
            Royal Lumina
          </h1>
          <p style={{
            fontSize: 10,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: "rgba(232,208,169,0.45)",
            margin: 0,
          }}>
            Luxury Handcrafted Candles
          </p>
        </div>
      </div>
    </div>
  );
}
